// Pricing-Override für Aivy-Admins, nur in diesem Browser. Admins schalten
// damit die Sperre an oder aus und simulieren ein Paket, ohne dass Kunden
// etwas davon sehen. Die Wirkung steckt in resolveEntitlements, hier liegt nur
// der Speicher.
//
// localStorage PRICING_ADMIN_OVERRIDES = { [partnerId]: { enforcement, plan } }
// Pro Partner, damit eine Simulation nicht mitwandert, wenn ein Admin zu einem
// anderen Kunden wechselt. signOut leert localStorage und damit auch das.
//
// Der Key steht hier und nicht in LOCALSTORAGE_ITEMS, weil helpers/index.js
// aws-amplify zieht und Jest bricht.
import { useCallback, useMemo, useSyncExternalStore } from 'react'

import {
  ADMIN_ENFORCEMENT,
  DEFAULT_ADMIN_OVERRIDE,
  REAL_PLAN,
  normalizeAdminOverride
} from './entitlements'
import { PLAN_ORDER } from './plans'

export const PRICING_ADMIN_OVERRIDES = 'PRICING_ADMIN_OVERRIDES'

// Auswahl im Admin-Dialog, in dieser Reihenfolge
export const ADMIN_ENFORCEMENT_OPTIONS = Object.freeze([
  ADMIN_ENFORCEMENT.FLAG,
  ADMIN_ENFORCEMENT.ON,
  ADMIN_ENFORCEMENT.OFF
])
export const ADMIN_PLAN_OPTIONS = Object.freeze([REAL_PLAN, ...PLAN_ORDER])

const EMPTY = Object.freeze({})

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

const isDefault = (override) =>
  override.enforcement === DEFAULT_ADMIN_OVERRIDE.enforcement &&
  override.plan === DEFAULT_ADMIN_OVERRIDE.plan

const readRaw = () => {
  try {
    return window.localStorage.getItem(PRICING_ADMIN_OVERRIDES)
  } catch {
    return null
  }
}

// Gecacht über den Rohstring, damit alle Hooks dasselbe Objekt sehen und
// useSyncExternalStore eine stabile Referenz bekommt
let cachedRaw
let cachedOverrides = EMPTY

const parseOverrides = (raw) => {
  if (raw === cachedRaw) return cachedOverrides
  let parsed = null
  try {
    parsed = raw ? JSON.parse(raw) : null
  } catch {
    parsed = null
  }
  cachedRaw = raw
  cachedOverrides = isPlainObject(parsed) ? parsed : EMPTY
  return cachedOverrides
}

// Kaputte oder unbekannte Werte ergeben den Default. Ein Override, der dem
// Default entspricht, ist genau DEFAULT_ADMIN_OVERRIDE, damit Vergleiche per
// Referenz reichen.
const overrideFor = (overrides, partnerId) => {
  if (!partnerId) return DEFAULT_ADMIN_OVERRIDE
  const entry = overrides[partnerId]
  if (!isPlainObject(entry)) return DEFAULT_ADMIN_OVERRIDE
  const normalized = normalizeAdminOverride(entry)
  return isDefault(normalized) ? DEFAULT_ADMIN_OVERRIDE : normalized
}

/** Alle gespeicherten Overrides, bei fehlendem oder kaputtem Wert `{}` */
export const readAdminOverrides = () => parseOverrides(readRaw())

/** Override für einen Partner, ohne Eintrag DEFAULT_ADMIN_OVERRIDE */
export const getAdminOverride = (partnerId) =>
  overrideFor(readAdminOverrides(), partnerId)

const listeners = new Set()

const emit = () => listeners.forEach((listener) => listener())

const subscribe = (listener) => {
  listeners.add(listener)
  // storage feuert nur in anderen Tabs. key null heißt localStorage.clear().
  const onStorage = (event) => {
    if (event.key === null || event.key === PRICING_ADMIN_OVERRIDES) listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

const writeOverrides = (overrides) => {
  try {
    if (Object.keys(overrides).length) {
      window.localStorage.setItem(
        PRICING_ADMIN_OVERRIDES,
        JSON.stringify(overrides)
      )
    } else {
      window.localStorage.removeItem(PRICING_ADMIN_OVERRIDES)
    }
  } catch {
    // Ohne localStorage bleibt alles beim Default
  }
  emit()
}

/**
 * Setzt Felder des Overrides für einen Partner. Unbekannte Werte werden
 * ignoriert. Entspricht das Ergebnis dem Default, verschwindet der Eintrag.
 *
 * @param {string} partnerId
 * @param {{ enforcement?: string, plan?: string }} patch
 */
export const setAdminOverride = (partnerId, patch = {}) => {
  if (!partnerId) return
  const valid = {}
  if (ADMIN_ENFORCEMENT_OPTIONS.includes(patch.enforcement)) {
    valid.enforcement = patch.enforcement
  }
  if (ADMIN_PLAN_OPTIONS.includes(patch.plan)) valid.plan = patch.plan
  if (!Object.keys(valid).length) return

  const overrides = readAdminOverrides()
  const next = { ...overrideFor(overrides, partnerId), ...valid }
  const all = { ...overrides }
  if (isDefault(next)) delete all[partnerId]
  else all[partnerId] = next
  writeOverrides(all)
}

/** Entfernt den Override für einen Partner */
export const resetAdminOverride = (partnerId) => {
  if (!partnerId) return
  const overrides = readAdminOverrides()
  if (!(partnerId in overrides)) return
  const { [partnerId]: removed, ...rest } = overrides
  writeOverrides(rest)
}

const subscribeNothing = () => () => {}
const readNothing = () => null

/**
 * Override des Admins für diesen Partner. Ohne Admin oder ohne partnerId wird
 * localStorage nicht gelesen, dann gilt DEFAULT_ADMIN_OVERRIDE und die Setter
 * tun nichts.
 *
 * `override` geht unverändert als `adminOverride` an resolveEntitlements.
 * `active` ist true, sobald der Override vom Default abweicht (Badge „aktiv").
 *
 * @param {{ isAdmin: boolean, partnerId: string }} params
 * @returns {{ available: boolean, active: boolean,
 *   override: { enforcement: string, plan: string },
 *   setEnforcement(value: string): void, setPlan(value: string): void,
 *   reset(): void }}
 */
export const usePricingAdminOverride = ({ isAdmin, partnerId } = {}) => {
  const available = !!isAdmin && !!partnerId

  const raw = useSyncExternalStore(
    available ? subscribe : subscribeNothing,
    available ? readRaw : readNothing,
    readNothing
  )

  const override = useMemo(
    () =>
      available
        ? overrideFor(parseOverrides(raw), partnerId)
        : DEFAULT_ADMIN_OVERRIDE,
    [available, raw, partnerId]
  )

  const setEnforcement = useCallback(
    (enforcement) => {
      if (available) setAdminOverride(partnerId, { enforcement })
    },
    [available, partnerId]
  )

  const setPlan = useCallback(
    (plan) => {
      if (available) setAdminOverride(partnerId, { plan })
    },
    [available, partnerId]
  )

  const reset = useCallback(() => {
    if (available) resetAdminOverride(partnerId)
  }, [available, partnerId])

  return useMemo(
    () => ({
      available,
      active: override !== DEFAULT_ADMIN_OVERRIDE,
      override,
      setEnforcement,
      setPlan,
      reset
    }),
    [available, override, setEnforcement, setPlan, reset]
  )
}
